import { useReducer, useCallback, useRef } from 'react';
import { calculateTotals, calculateStaffBreakdown, round2, PAYMENT_TYPES } from '../utils/checkoutCalculations';
import toast from '../../shared/Toast';

// Action types
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  LOAD_BOOKING: 'LOAD_BOOKING',
  SET_SCREEN: 'SET_SCREEN',
  ADD_LINE_ITEM: 'ADD_LINE_ITEM',
  REMOVE_LINE_ITEM: 'REMOVE_LINE_ITEM',
  UPDATE_LINE_ITEM_PRICE: 'UPDATE_LINE_ITEM_PRICE',
  UPDATE_LINE_ITEM: 'UPDATE_LINE_ITEM',
  SET_LINE_ITEM_QUANTITY: 'SET_LINE_ITEM_QUANTITY',
  SET_ITEM_DISCOUNT: 'SET_ITEM_DISCOUNT',
  SET_CART_DISCOUNT: 'SET_CART_DISCOUNT',
  CLEAR_DISCOUNT: 'CLEAR_DISCOUNT',
  SET_TIP: 'SET_TIP',
  SET_TAX: 'SET_TAX',
  ADD_TRANSACTION: 'ADD_TRANSACTION',
  ADD_EXISTING_TRANSACTION: 'ADD_EXISTING_TRANSACTION',
  SET_PENDING_DEPOSIT: 'SET_PENDING_DEPOSIT',
  REMOVE_TRANSACTION: 'REMOVE_TRANSACTION',
  SET_AMOUNT_MODE: 'SET_AMOUNT_MODE',
  SET_CUSTOM_AMOUNT: 'SET_CUSTOM_AMOUNT',
  SET_PAYMENT_TYPE: 'SET_PAYMENT_TYPE',
  SET_PROCESSING: 'SET_PROCESSING',
  SET_PAYMENT_METHODS: 'SET_PAYMENT_METHODS',
  MARK_COMPLETE: 'MARK_COMPLETE',
  ADD_VOUCHER_TO_SELL: 'ADD_VOUCHER_TO_SELL',
  REMOVE_VOUCHER_TO_SELL: 'REMOVE_VOUCHER_TO_SELL',
  SET_LOYALTY_REWARDS: 'SET_LOYALTY_REWARDS',
  APPLY_PROMO: 'APPLY_PROMO',
  REMOVE_PROMO: 'REMOVE_PROMO',
  RESET: 'RESET',
};

const initialState = {
  isLoading: true,
  error: null,

  // Booking data
  booking: null,
  bookingId: null,
  client: null,
  salonId: null,

  // Line items (services + products)
  lineItems: [],

  // Transactions
  transactions: [],
  existingTransactions: [],

  // Tip
  tip: { amount: 0, type: null },

  // Cart-level discount
  cartDiscount: null,

  // A discount code on the sale: { code, codeId, type, value, amount, lines: [{ index, amount }] }.
  // Its saving sits on each line it covers (discount with promoLine: true).
  // promoDropped names a code taken off because the sale changed after it.
  promo: null,
  promoDropped: null,

  // Vouchers being sold
  vouchersToSell: [],

  // Loyalty rewards this client can redeem at THIS salon (rewardsAvailable > 0).
  // Loaded once the booking's client is known; drives the till redeem option.
  loyaltyRewards: [],

  // Derived (recalculated via recalc helper)
  subtotal: 0,
  itemDiscountTotal: 0,
  cartDiscountAmount: 0,
  discountTotal: 0,
  // Tax added ON TOP of the balance (exclusive mode only).
  taxAmount: 0,
  tipAmount: 0,
  depositsApplied: 0,
  totalDue: 0,
  totalPaid: 0,
  balanceRemaining: 0,
  staffBreakdown: [],

  // Payment
  amountMode: 'balance',
  customAmount: '',
  selectedPaymentType: null,
  isProcessingPayment: false,

  // When set, the cardReader screen is being used to take a deposit (not a
  // regular session payment). Cleared once the deposit is processed.
  pendingDepositAmount: null,

  // UI
  screen: 'summary',
  isComplete: false,

  // Settings
  paymentMethods: {},
  currencySymbol: '£',
};

/**
 * Recalculate all derived totals from current state.
 */
function recalc(state) {
  const allItems = [
    ...state.lineItems,
    ...state.vouchersToSell.map(v => ({ originalPrice: v.value, discount: null })),
  ];

  const totals = calculateTotals({
    lineItems: allItems,
    cartDiscount: state.cartDiscount,
    transactions: state.transactions,
    existingTransactions: state.existingTransactions,
    tip: state.tip,
    taxAmount: state.taxAmount,
  });

  const staffBreakdown = calculateStaffBreakdown(state.lineItems, state.tip);

  return { ...state, ...totals, staffBreakdown };
}

/**
 * Parse a booking API response into line items and existing transactions.
 */
function parseBookingData(bookingData) {
  const serviceItems = (bookingData.servicesBooked || []).map((svc, i) => ({
    type: 'service',
    id: `service_${i}`,
    serviceId: svc.service?.serviceId || svc.service?._id || svc.serviceId,
    // The salon's service _id and the shared catalogue id are DIFFERENT ids.
    // Loyalty/discount item rules are stored against commonServiceId, so carry
    // it through or those rules can never match a booked line.
    commonServiceId: svc.service?.commonServiceId || svc.commonServiceId || null,
    name: svc.service?.serviceName || svc.serviceName || '',
    originalPrice: round2(parseFloat(svc.servicePrice) || 0),
    staffId: svc.hairdresserId || bookingData.hairdresserId || bookingData.hairdresserInfo?._id,
    staffName: svc.hairdresserName || bookingData.hairdresser?.name || bookingData.hairdresser?.hairdresser?.name || bookingData.hairdresserName || bookingData.hairdresserInfo?.name || '',
    quantity: 1,
    discount: null,
    addedDuringCheckout: false,
    diaryColour: svc.service?.diaryColour || svc.diaryColour || null,
    serviceTime: svc.serviceTime || 0,
  }));

  // Parse products attached to the booking
  const productItems = (bookingData.products || []).map((prod, i) => ({
    type: 'product',
    id: `product_${i}`,
    serviceId: prod.productId || prod._id,
    name: prod.productName || prod.name || '',
    originalPrice: round2(parseFloat(prod.retailPrice || prod.price) || 0),
    staffId: bookingData.hairdresserId,
    staffName: bookingData.hairdresser?.name || bookingData.hairdresser?.hairdresser?.name || bookingData.hairdresserName || bookingData.hairdresserInfo?.name || '',
    quantity: 1,
    discount: null,
    addedDuringCheckout: false,
    diaryColour: null,
    stockPrice: prod.stockPrice || 0,
    commissionEnable: prod.commissionEnable || 0,
  }));

  const lineItems = [...serviceItems, ...productItems];

  // Parse existing transactions (deposits + prior payments).
  // Preserve `createdAt` from the source so timestamps don't collapse onto
  // the final-checkout date when the FE re-merges these on save.
  const existingTransactions = [];
  if (bookingData.transaction?.length > 0) {
    bookingData.transaction.forEach(t => {
      if (t.paymentType !== PAYMENT_TYPES.CARD_READER) {
        existingTransactions.push({
          paymentType: t.paymentType,
          amount: round2(parseFloat(t.amount) || 0),
          label: t.paymentType === PAYMENT_TYPES.VOUCHER ? 'Gift card' : 'Payment',
          isDeposit: t.pgObjectType === 'deposit',
          createdAt: t.createdAt || null,
          // Gift-card linkage on voucher rows — carried through the
          // final-checkout merge so the redemption stays traceable.
          ...(t.voucherInstanceId ? { voucherInstanceId: t.voucherInstanceId } : {}),
          ...(t.voucherCode ? { voucherCode: t.voucherCode } : {}),
          ...(t.catalogId ? { catalogId: t.catalogId } : {}),
        });
      }
    });
  }

  if (bookingData.transactions?.length > 0) {
    // A deposit exists in BOTH sources once a checkout has saved: the
    // transactions COLLECTION row (written when the deposit was taken) and an
    // embedded booking.transaction[] leg (written by the checkout merge).
    // Counting both doubled the deposit on every subsequent checkout load —
    // depositAmount derived £20 off a £10 deposit, the client was undercharged
    // by the difference, and the (correct) transfer legs then exceeded the
    // undersized capture and failed at Stripe. If the embedded legs already
    // carry a deposit, the collection copy is a duplicate: skip it.
    // Pairwise, amount-matched: each collection deposit is only skipped if an
    // unconsumed embedded deposit of the SAME amount exists. This dedupes the
    // online-deposit copy without hiding a second, different deposit (e.g. a
    // deposit link plus an in-salon reader deposit on one booking). Cash and
    // reader deposits are untouched: cash has no collection row, and reader
    // legs never hydrate from the embedded source in the first place.
    const embeddedDepositAmounts = existingTransactions
      .filter((t) => t.isDeposit)
      .map((t) => round2(t.amount));
    bookingData.transactions.forEach(t => {
      let isDuplicateDeposit = false;
      if (t.pgObjectType === 'deposit') {
        const idx = embeddedDepositAmounts.indexOf(round2(parseFloat(t.pgTransactionAmount) || 0));
        if (idx !== -1) {
          embeddedDepositAmounts.splice(idx, 1);
          isDuplicateDeposit = true;
        }
      }
      // Only count actual payments - skip booking records like "cashCollected"
      // A real payment has pgTransactionStatus "succeeded" and is a deposit or charge
      const isRealPayment = t.pgTransactionStatus === 'succeeded'
        && t.pgObjectType !== 'cashCollected'
        && parseFloat(t.pgTransactionAmount) > 0
        && !isDuplicateDeposit;
      if (isRealPayment) {
        // Detect card-reader vs online from the captured paymentIntent. Stripe
        // Terminal sets payment_method_types: ['card_present']; online deposits
        // (Stripe Checkout from createPaymentLink/paymentCheckout) don't.
        // Without this check, online deposits got tagged as Beu Terminal in
        // booking.transaction[] and the breakdown UI mislabelled them.
        const methodTypes = t.paymentIntentsObject?.payment_method_types || [];
        const isCardReader = methodTypes.includes('card_present');
        // Original capture time, preferring Stripe's paymentIntent.created
        // (when the customer actually paid) over the transaction record's
        // createdTime (when our BE first inserted the row, which can lag).
        const stripeCreated = t.paymentIntentsObject?.created;
        const stripeCreatedIso = stripeCreated ? new Date(stripeCreated * 1000).toISOString() : null;
        existingTransactions.push({
          paymentType: isCardReader ? PAYMENT_TYPES.CARD_READER : PAYMENT_TYPES.APP,
          amount: round2(parseFloat(t.pgTransactionAmount) || 0),
          label: t.pgObjectType === 'deposit' ? 'Deposit' : 'Card Payment',
          isDeposit: t.pgObjectType === 'deposit',
          createdAt: stripeCreatedIso || t.createdTime || null,
        });
      }
    });
  }

  const client = bookingData.user || bookingData.clientInfo || null;
  const salonId = bookingData.salonId || localStorage.getItem('salonId');

  return { lineItems, existingTransactions, client, salonId };
}

function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };

    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };

    case ACTIONS.LOAD_BOOKING: {
      const bookingData = action.payload;
      const { lineItems, existingTransactions, client, salonId } = parseBookingData(bookingData);
      return recalc({
        ...state,
        isLoading: false,
        error: null,
        booking: bookingData,
        bookingId: bookingData._id,
        client,
        salonId,
        lineItems,
        existingTransactions,
        currencySymbol: bookingData.currencySymbol || '£',
      });
    }

    case ACTIONS.SET_SCREEN:
      return { ...state, screen: action.payload };

    case ACTIONS.ADD_LINE_ITEM:
      return recalc({
        ...state,
        lineItems: [...state.lineItems, { ...action.payload, quantity: action.payload.quantity || 1, addedDuringCheckout: true }],
      });

    case ACTIONS.REMOVE_LINE_ITEM:
      return recalc({
        ...state,
        lineItems: state.lineItems.filter((_, i) => i !== action.payload),
      });

    case ACTIONS.UPDATE_LINE_ITEM_PRICE: {
      const { index, price } = action.payload;
      const updated = state.lineItems.map((item, i) =>
        i === index ? { ...item, listPrice: item.listPrice != null ? item.listPrice : item.originalPrice, originalPrice: round2(parseFloat(price) || 0) } : item
      );
      return recalc({ ...state, lineItems: updated });
    }

    // Patch fields on one line — used when a variant is chosen in the sidebar,
    // which can also change the line's price (a variant may override it).
    case ACTIONS.UPDATE_LINE_ITEM: {
      const { index, patch } = action.payload;
      return recalc({
        ...state,
        lineItems: state.lineItems.map((item, i) => i === index ? { ...item, ...patch } : item),
      });
    }

    case ACTIONS.SET_LINE_ITEM_QUANTITY: {
      const { index, quantity } = action.payload;
      const q = Math.max(1, Math.round(quantity));
      const updated = state.lineItems.map((item, i) =>
        i === index ? { ...item, quantity: q } : item
      );
      return recalc({ ...state, lineItems: updated });
    }

    case ACTIONS.SET_ITEM_DISCOUNT: {
      const { index, discount } = action.payload;
      const updated = state.lineItems.map((item, i) =>
        i === index ? { ...item, discount } : item
      );
      return recalc({ ...state, lineItems: updated });
    }

    // One discount per sale: a manual discount replaces a code
    case ACTIONS.SET_CART_DISCOUNT:
      return recalc({
        ...state,
        cartDiscount: action.payload,
        promo: null,
        lineItems: state.promo ? state.lineItems.map(item => (item.promoLine ? { ...item, discount: null, promoLine: undefined } : item)) : state.lineItems,
      });

    case ACTIONS.CLEAR_DISCOUNT:
      return recalc({
        ...state,
        cartDiscount: null,
        promo: null,
        lineItems: state.lineItems.map(item => ({ ...item, discount: null, promoLine: undefined })),
      });

    // A checked code: its saving goes on each line it covers, and it replaces
    // any manual discount (never both)
    case ACTIONS.APPLY_PROMO: {
      const { promo, lineDiscounts } = action.payload;
      return recalc({
        ...state,
        cartDiscount: null,
        promo,
        promoDropped: null,
        lineItems: state.lineItems.map((item, i) => (lineDiscounts[i]
          ? { ...item, discount: lineDiscounts[i], promoLine: true }
          : { ...item, discount: null, promoLine: undefined })),
      });
    }

    case ACTIONS.REMOVE_PROMO:
      return recalc({
        ...state,
        promo: null,
        promoDropped: null,
        lineItems: state.lineItems.map(item => (item.promoLine ? { ...item, discount: null, promoLine: undefined } : item)),
      });

    case ACTIONS.SET_TIP:
      return recalc({ ...state, tip: action.payload });

    case ACTIONS.SET_TAX:
      return recalc({ ...state, taxAmount: round2(action.payload || 0) });

    case ACTIONS.ADD_TRANSACTION: {
      const newState = recalc({
        ...state,
        transactions: [...state.transactions, action.payload],
        isProcessingPayment: false,
        // Reset amount selection so next payment defaults to remaining balance
        amountMode: 'balance',
        customAmount: '',
      });
      // Always return to summary for review. User explicitly presses "Complete".
      return { ...newState, screen: 'summary' };
    }

    case ACTIONS.ADD_EXISTING_TRANSACTION:
      // Used after a deposit save to push the deposit row into the
      // existingTransactions bucket. No screen-jump side effects (unlike
      // ADD_TRANSACTION which is for in-session payments).
      return recalc({
        ...state,
        existingTransactions: [...state.existingTransactions, action.payload],
      });

    case ACTIONS.SET_PENDING_DEPOSIT:
      return { ...state, pendingDepositAmount: action.payload };

    case ACTIONS.REMOVE_TRANSACTION:
      return recalc({
        ...state,
        transactions: state.transactions.filter((_, i) => i !== action.payload),
      });

    case ACTIONS.SET_AMOUNT_MODE:
      return { ...state, amountMode: action.payload, customAmount: '' };

    case ACTIONS.SET_CUSTOM_AMOUNT:
      return { ...state, customAmount: action.payload };

    case ACTIONS.SET_PAYMENT_TYPE:
      return { ...state, selectedPaymentType: action.payload };

    case ACTIONS.SET_PROCESSING:
      return { ...state, isProcessingPayment: action.payload };

    case ACTIONS.SET_PAYMENT_METHODS:
      return { ...state, paymentMethods: action.payload };

    case ACTIONS.MARK_COMPLETE:
      return { ...state, screen: 'complete', isComplete: true };

    case ACTIONS.ADD_VOUCHER_TO_SELL:
      return recalc({
        ...state,
        vouchersToSell: [...state.vouchersToSell, action.payload],
      });

    case ACTIONS.REMOVE_VOUCHER_TO_SELL:
      return recalc({
        ...state,
        vouchersToSell: state.vouchersToSell.filter((_, i) => i !== action.payload),
      });

    case ACTIONS.SET_LOYALTY_REWARDS:
      return { ...state, loyaltyRewards: action.payload || [] };

    case ACTIONS.RESET:
      return { ...initialState };

    default:
      return state;
  }
}

// A code's saving was worked out for the sale as it was. When the sale
// changes the code comes off, and the summary says so (it can go back on).
const SALE_CHANGES = [ACTIONS.ADD_LINE_ITEM, ACTIONS.REMOVE_LINE_ITEM, ACTIONS.UPDATE_LINE_ITEM_PRICE, ACTIONS.UPDATE_LINE_ITEM, ACTIONS.SET_LINE_ITEM_QUANTITY, ACTIONS.ADD_VOUCHER_TO_SELL, ACTIONS.REMOVE_VOUCHER_TO_SELL];
export function withPromo(state, action) {
  const next = reducer(state, action);
  if (!state.promo || !SALE_CHANGES.includes(action.type)) return next;
  return recalc({
    ...next,
    promo: null,
    promoDropped: state.promo.code,
    lineItems: next.lineItems.map(item => (item.promoLine ? { ...item, discount: null, promoLine: undefined } : item)),
  });
}

/**
 * Custom hook for checkout state management.
 * @returns {[Object, Function]} [state, dispatch]
 */
export default function useCheckoutState() {
  const [state, dispatch] = useReducer(withPromo, initialState);
  // The balance as of the latest render. Card reader, card on file and payment
  // links add their payment and then auto-complete from an async handler that
  // still holds the actions from BEFORE the payment, so a closure over
  // state.balanceRemaining saw the unpaid balance and refused to complete
  // ("£28.00 outstanding" right after a successful reader payment).
  const balanceRef = useRef(state.balanceRemaining);
  balanceRef.current = state.balanceRemaining;

  // Convenience dispatchers
  const actions = {
    setLoading: useCallback((v) => dispatch({ type: ACTIONS.SET_LOADING, payload: v }), []),
    setError: useCallback((v) => dispatch({ type: ACTIONS.SET_ERROR, payload: v }), []),
    loadBooking: useCallback((data) => dispatch({ type: ACTIONS.LOAD_BOOKING, payload: data }), []),
    setScreen: useCallback((s) => dispatch({ type: ACTIONS.SET_SCREEN, payload: s }), []),
    addLineItem: useCallback((item) => dispatch({ type: ACTIONS.ADD_LINE_ITEM, payload: item }), []),
    removeLineItem: useCallback((i) => dispatch({ type: ACTIONS.REMOVE_LINE_ITEM, payload: i }), []),
    updateLineItemPrice: useCallback((index, price) => dispatch({ type: ACTIONS.UPDATE_LINE_ITEM_PRICE, payload: { index, price } }), []),
    setLineItemQuantity: useCallback((index, quantity) => dispatch({ type: ACTIONS.SET_LINE_ITEM_QUANTITY, payload: { index, quantity } }), []),
    updateLineItem: useCallback((index, patch) => dispatch({ type: ACTIONS.UPDATE_LINE_ITEM, payload: { index, patch } }), []),
    setItemDiscount: useCallback((index, discount) => dispatch({ type: ACTIONS.SET_ITEM_DISCOUNT, payload: { index, discount } }), []),
    setCartDiscount: useCallback((d) => dispatch({ type: ACTIONS.SET_CART_DISCOUNT, payload: d }), []),
    clearDiscount: useCallback(() => dispatch({ type: ACTIONS.CLEAR_DISCOUNT }), []),
    applyPromo: useCallback((promo, lineDiscounts) => dispatch({ type: ACTIONS.APPLY_PROMO, payload: { promo, lineDiscounts } }), []),
    removePromo: useCallback(() => dispatch({ type: ACTIONS.REMOVE_PROMO }), []),
    setTip: useCallback((tip) => dispatch({ type: ACTIONS.SET_TIP, payload: tip }), []),
    setTax: useCallback((amount) => dispatch({ type: ACTIONS.SET_TAX, payload: amount }), []),
    addTransaction: useCallback((t) => dispatch({ type: ACTIONS.ADD_TRANSACTION, payload: t }), []),
    addExistingTransaction: useCallback((t) => dispatch({ type: ACTIONS.ADD_EXISTING_TRANSACTION, payload: t }), []),
    setPendingDeposit: useCallback((amt) => dispatch({ type: ACTIONS.SET_PENDING_DEPOSIT, payload: amt }), []),
    removeTransaction: useCallback((i) => dispatch({ type: ACTIONS.REMOVE_TRANSACTION, payload: i }), []),
    setAmountMode: useCallback((m) => dispatch({ type: ACTIONS.SET_AMOUNT_MODE, payload: m }), []),
    setCustomAmount: useCallback((a) => dispatch({ type: ACTIONS.SET_CUSTOM_AMOUNT, payload: a }), []),
    setPaymentType: useCallback((t) => dispatch({ type: ACTIONS.SET_PAYMENT_TYPE, payload: t }), []),
    setProcessing: useCallback((v) => dispatch({ type: ACTIONS.SET_PROCESSING, payload: v }), []),
    setPaymentMethods: useCallback((m) => dispatch({ type: ACTIONS.SET_PAYMENT_METHODS, payload: m }), []),
    markComplete: useCallback(() => {
      // Don't allow completion with an outstanding balance. The legit way to
      // finish a £0 booking is to discount / zero-out the line items until
      // balanceRemaining hits zero. Without this guard, clicking "Complete"
      // just flips bookingStatus to 4 without ever collecting the balance —
      // leaving unrecorded receivables behind. Reads the ref, not a closure:
      // see balanceRef above.
      const balance = balanceRef.current || 0;
      if (balance > 0.01) {
        toast.error(`Can't complete. £${balance.toFixed(2)} outstanding. Take payment or set the price to £0.`);
        return;
      }
      dispatch({ type: ACTIONS.MARK_COMPLETE });
    }, []),
    addVoucherToSell: useCallback((v) => dispatch({ type: ACTIONS.ADD_VOUCHER_TO_SELL, payload: v }), []),
    removeVoucherToSell: useCallback((i) => dispatch({ type: ACTIONS.REMOVE_VOUCHER_TO_SELL, payload: i }), []),
    setLoyaltyRewards: useCallback((r) => dispatch({ type: ACTIONS.SET_LOYALTY_REWARDS, payload: r }), []),
    reset: useCallback(() => dispatch({ type: ACTIONS.RESET }), []),
  };

  return [state, actions];
}

export { ACTIONS, initialState, reducer, recalc };
